/**
 * Global Theme Configuration
 * 
 * Comprehensive theming system with light/dark mode support,
 * automatic contrast detection, and CSS variable integration.
 */

import type { CSSProperties } from 'react';

// Color palette type definitions
interface ColorShade {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

interface ThemeColors {
  primary: ColorShade;
  secondary: ColorShade;
  accent: ColorShade;
  neutral: ColorShade;
  success: ColorShade;
  warning: ColorShade;
  error: ColorShade;
  info: ColorShade;
}

interface ThemeMode {
  // Background colors
  background: {
    primary: string;    // Main background
    secondary: string;  // Card/section backgrounds
    tertiary: string;   // Subtle backgrounds
    elevated: string;   // Elevated surfaces
    overlay: string;    // Modal overlays
  };
  
  // Text colors
  text: {
    primary: string;    // Main text
    secondary: string;  // Secondary text
    tertiary: string;   // Muted text
    inverse: string;    // Inverse text
    disabled: string;   // Disabled text
    link: string;       // Link text
    linkHover: string;  // Link hover
  };
  
  // Border colors
  border: {
    default: string;    // Default borders
    light: string;      // Light borders
    strong: string;     // Strong borders
    focus: string;      // Focus borders
  };
  
  // Component-specific colors
  components: {
    button: {
      primary: string;
      primaryHover: string;
      secondary: string;
      secondaryHover: string;
    };
    input: {
      background: string;
      border: string;
      focusBorder: string;
      text: string;
      placeholder: string;
    };
    table: {
      headerBg: string;
      rowBg: string;
      rowHoverBg: string;
      rowSelectedBg: string;
      border: string;
    };
    modal: {
      background: string;
      overlay: string;
    };
  };
}

interface ThemeConfig {
  colors: ThemeColors;
  light: ThemeMode;
  dark: ThemeMode;
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  typography: {
    fontFamily: {
      sans: string;
      serif: string;
      mono: string;
    };
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
      '4xl': string;
      '5xl': string;
    };
    fontWeight: {
      thin: number;
      light: number;
      normal: number;
      medium: number;
      semibold: number;
      bold: number;
      extrabold: number;
    };
    lineHeight: {
      none: number;
      tight: number;
      snug: number;
      normal: number;
      relaxed: number;
      loose: number;
    };
  };
  borderRadius: {
    none: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
    full: string;
  };
  shadows: {
    none: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    inner: string;
  };
  transitions: {
    fast: string;
    normal: string;
    slow: string;
  };
  breakpoints: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
}

// Main theme configuration
export const themeConfig: ThemeConfig = {
  // Color palette (consistent across light/dark modes)
  colors: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
    secondary: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
    },
    accent: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
      950: '#4a044e',
    },
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0a0a0a',
    },
    success: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16',
    },
    warning: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
      950: '#451a03',
    },
    error: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
      950: '#450a0a',
    },
    info: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
  },

  // Light mode theme
  light: {
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
      elevated: '#ffffff',
      overlay: 'rgba(0, 0, 0, 0.5)',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      tertiary: '#6b7280',
      inverse: '#ffffff',
      disabled: '#9ca3af',
      link: '#3b82f6',
      linkHover: '#2563eb',
    },
    border: {
      default: '#e5e7eb',
      light: '#f3f4f6',
      strong: '#d1d5db',
      focus: '#3b82f6',
    },
    components: {
      button: {
        primary: '#3b82f6',
        primaryHover: '#2563eb',
        secondary: '#f3f4f6',
        secondaryHover: '#e5e7eb',
      },
      input: {
        background: '#ffffff',
        border: '#d1d5db',
        focusBorder: '#3b82f6',
        text: '#111827',
        placeholder: '#9ca3af',
      },
      table: {
        headerBg: '#f9fafb',
        rowBg: '#ffffff',
        rowHoverBg: '#eff6ff',
        rowSelectedBg: '#dbeafe',
        border: '#e5e7eb',
      },
      modal: {
        background: '#ffffff',
        overlay: 'rgba(0, 0, 0, 0.5)',
      },
    },
  },

  // Dark mode theme
  dark: {
    background: {
      primary: '#0f172a',
      secondary: '#1e293b',
      tertiary: '#334155',
      elevated: '#1e293b',
      overlay: 'rgba(0, 0, 0, 0.75)',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
      tertiary: '#94a3b8',
      inverse: '#0f172a',
      disabled: '#64748b',
      link: '#60a5fa',
      linkHover: '#93c5fd',
    },
    border: {
      default: '#334155',
      light: '#1e293b',
      strong: '#475569',
      focus: '#60a5fa',
    },
    components: {
      button: {
        primary: '#3b82f6',
        primaryHover: '#60a5fa',
        secondary: '#334155',
        secondaryHover: '#475569',
      },
      input: {
        background: '#1e293b',
        border: '#334155',
        focusBorder: '#60a5fa',
        text: '#f1f5f9',
        placeholder: '#64748b',
      },
      table: {
        headerBg: '#1e293b',
        rowBg: '#0f172a',
        rowHoverBg: '#1e293b',
        rowSelectedBg: '#334155',
        border: '#334155',
      },
      modal: {
        background: '#1e293b',
        overlay: 'rgba(0, 0, 0, 0.75)',
      },
    },
  },

  // Spacing system
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },

  // Typography system
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
      mono: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
    },
    fontWeight: {
      thin: 100,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },
  },

  // Border radius system
  borderRadius: {
    none: '0',
    sm: '0.125rem',    // 2px
    md: '0.375rem',    // 6px
    lg: '0.5rem',      // 8px
    xl: '0.75rem',     // 12px
    '2xl': '1rem',     // 16px
    '3xl': '1.5rem',   // 24px
    full: '9999px',
  },

  // Shadow system
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  },

  // Transition system
  transitions: {
    fast: '150ms ease-in-out',
    normal: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },

  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

/**
 * Utility function to get contrast ratio between two colors
 */
export function getContrastRatio(color1: string, color2: string): number {
  const getLuminance = (color: string): number => {
    const rgb = color.match(/\d+/g);
    if (!rgb) return 0;
    
    const [r, g, b] = rgb.map(c => {
      const val = parseInt(c) / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Automatically determine if text should be light or dark based on background
 */
export function getContrastText(backgroundColor: string): string {
  // Convert hex to RGB if needed
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    } : null;
  };

  // Calculate perceived brightness
  const getBrightness = (color: string): number => {
    let rgb: { r: number; g: number; b: number } | null = null;
    
    if (color.startsWith('#')) {
      rgb = hexToRgb(color);
    } else if (color.startsWith('rgb')) {
      const values = color.match(/\d+/g);
      if (values && values.length >= 3) {
        rgb = {
          r: parseInt(values[0]),
          g: parseInt(values[1]),
          b: parseInt(values[2]),
        };
      }
    }
    
    if (!rgb) return 128; // Default to middle brightness
    
    // Using perceived brightness formula
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  };

  const brightness = getBrightness(backgroundColor);
  
  // Return light text for dark backgrounds, dark text for light backgrounds
  return brightness > 128 ? themeConfig.light.text.primary : themeConfig.dark.text.primary;
}

/**
 * Get theme variables for CSS
 */
export function getThemeVariables(mode: 'light' | 'dark'): Record<string, string> {
  const theme = themeConfig[mode];
  
  return {
    // Background colors
    '--theme-bg-primary': theme.background.primary,
    '--theme-bg-secondary': theme.background.secondary,
    '--theme-bg-tertiary': theme.background.tertiary,
    '--theme-bg-elevated': theme.background.elevated,
    '--theme-bg-overlay': theme.background.overlay,
    
    // Text colors
    '--theme-text-primary': theme.text.primary,
    '--theme-text-secondary': theme.text.secondary,
    '--theme-text-tertiary': theme.text.tertiary,
    '--theme-text-inverse': theme.text.inverse,
    '--theme-text-disabled': theme.text.disabled,
    '--theme-text-link': theme.text.link,
    '--theme-text-link-hover': theme.text.linkHover,
    
    // Border colors
    '--theme-border-default': theme.border.default,
    '--theme-border-light': theme.border.light,
    '--theme-border-strong': theme.border.strong,
    '--theme-border-focus': theme.border.focus,
    
    // Component colors
    '--theme-button-primary': theme.components.button.primary,
    '--theme-button-primary-hover': theme.components.button.primaryHover,
    '--theme-button-secondary': theme.components.button.secondary,
    '--theme-button-secondary-hover': theme.components.button.secondaryHover,
    
    '--theme-input-bg': theme.components.input.background,
    '--theme-input-border': theme.components.input.border,
    '--theme-input-focus-border': theme.components.input.focusBorder,
    '--theme-input-text': theme.components.input.text,
    '--theme-input-placeholder': theme.components.input.placeholder,
    
    '--theme-table-header-bg': theme.components.table.headerBg,
    '--theme-table-row-bg': theme.components.table.rowBg,
    '--theme-table-row-hover-bg': theme.components.table.rowHoverBg,
    '--theme-table-row-selected-bg': theme.components.table.rowSelectedBg,
    '--theme-table-border': theme.components.table.border,
    
    '--theme-modal-bg': theme.components.modal.background,
    '--theme-modal-overlay': theme.components.modal.overlay,
    
    // Spacing
    '--spacing-xs': themeConfig.spacing.xs,
    '--spacing-sm': themeConfig.spacing.sm,
    '--spacing-md': themeConfig.spacing.md,
    '--spacing-lg': themeConfig.spacing.lg,
    '--spacing-xl': themeConfig.spacing.xl,
    '--spacing-2xl': themeConfig.spacing['2xl'],
    '--spacing-3xl': themeConfig.spacing['3xl'],
    
    // Typography
    '--font-sans': themeConfig.typography.fontFamily.sans,
    '--font-serif': themeConfig.typography.fontFamily.serif,
    '--font-mono': themeConfig.typography.fontFamily.mono,
    
    // Border radius
    '--radius-sm': themeConfig.borderRadius.sm,
    '--radius-md': themeConfig.borderRadius.md,
    '--radius-lg': themeConfig.borderRadius.lg,
    '--radius-xl': themeConfig.borderRadius.xl,
    
    // Transitions
    '--transition-fast': themeConfig.transitions.fast,
    '--transition-normal': themeConfig.transitions.normal,
    '--transition-slow': themeConfig.transitions.slow,
  };
}

/**
 * Apply theme to a component's styles
 */
export function applyTheme(
  baseStyles: CSSProperties,
  _mode: 'light' | 'dark'
): CSSProperties {
  // Automatically adjust text color if background is set
  if (baseStyles.backgroundColor && !baseStyles.color) {
    baseStyles.color = getContrastText(baseStyles.backgroundColor as string);
  }

  return baseStyles;
}

/**
 * Theme-aware component styles helper
 */
export function getThemedStyles(
  component: keyof ThemeMode['components'],
  mode: 'light' | 'dark'
): CSSProperties {
  const theme = themeConfig[mode];
  const componentTheme = theme.components[component];
  
  const styles: CSSProperties = {};
  
  switch (component) {
    case 'button': {
      const c = componentTheme as ThemeMode['components']['button'];
      styles.backgroundColor = c.primary;
      styles.color = getContrastText(c.primary);
      break;
    }
    case 'input': {
      const c = componentTheme as ThemeMode['components']['input'];
      styles.backgroundColor = c.background;
      styles.borderColor = c.border;
      styles.color = c.text;
      break;
    }
    case 'table': {
      const c = componentTheme as ThemeMode['components']['table'];
      styles.backgroundColor = c.rowBg;
      styles.borderColor = c.border;
      break;
    }
    case 'modal': {
      const c = componentTheme as ThemeMode['components']['modal'];
      styles.backgroundColor = c.background;
      styles.color = theme.text.primary;
      break;
    }
  }
  
  return styles;
}

// Export theme mode type for use in components
export type { ThemeMode, ThemeColors };
export default themeConfig;